import type { AgeGroup, Language } from '../../types'

export interface WordEntry {
  word: string   // UPPERCASE
  clue: string
}

// ─── English ──────────────────────────────────────────────────────────────────

const EN_TINY: WordEntry[] = [
  { word: 'CAT',  clue: 'A furry pet that purrs and says meow' },
  { word: 'DOG',  clue: 'A loyal pet that wags its tail and barks' },
  { word: 'SUN',  clue: 'The bright star that shines in the sky by day' },
  { word: 'BEE',  clue: 'A striped flying insect that makes honey' },
  { word: 'EGG',  clue: 'A round shell that baby birds hatch from' },
  { word: 'ANT',  clue: 'A tiny insect that carries food back to its colony' },
  { word: 'HAT',  clue: 'A covering you wear on top of your head' },
  { word: 'CUP',  clue: 'A small container you drink from' },
  { word: 'OWL',  clue: 'A wise bird that hoots and hunts at night' },
  { word: 'PIG',  clue: 'A pink farm animal that loves to roll in mud' },
  { word: 'COW',  clue: 'A large farm animal that gives us milk' },
  { word: 'HEN',  clue: 'A female chicken that lays eggs' },
  { word: 'BUS',  clue: 'A big yellow or red vehicle that carries many passengers' },
  { word: 'MAP',  clue: 'A flat picture that shows roads and places' },
  { word: 'PEN',  clue: 'A thin tool you use to write with ink' },
  { word: 'BED',  clue: 'The cosy place where you sleep at night' },
  { word: 'NET',  clue: 'A mesh of strings used to catch fish or butterflies' },
  { word: 'FAN',  clue: 'It spins around to keep you cool on a hot day' },
  { word: 'LOG',  clue: 'A thick piece cut from a tree trunk' },
  { word: 'JAR',  clue: 'A wide glass container with a lid, used for jam' },
  { word: 'JET',  clue: 'A very fast aeroplane with jet engines' },
  { word: 'NUT',  clue: 'A hard-shelled snack that squirrels love to collect' },
  { word: 'POT',  clue: 'A deep round container used to cook soup or pasta' },
  { word: 'RAT',  clue: 'A large grey rodent with a long thin tail' },
  { word: 'VAN',  clue: 'A boxy vehicle used to deliver parcels or move furniture' },
  { word: 'FOX',  clue: 'A clever red animal with a bushy tail' },
  { word: 'BUG',  clue: 'A small creeping insect' },
  { word: 'BOX',  clue: 'A square container you can put things inside' },
  { word: 'HOT',  clue: 'The opposite of cold; what fire feels like' },
  { word: 'WET',  clue: 'Covered in water; how you feel after the rain' },
  { word: 'BIG',  clue: 'Very large in size' },
  { word: 'RED',  clue: 'The colour of a fire engine or a ripe tomato' },
  { word: 'YAK',  clue: 'A large furry ox that lives high in the mountains' },
  { word: 'ZAP',  clue: 'To hit suddenly with electricity or a laser beam' },
  { word: 'DEN',  clue: 'A cosy room or a wild animal\'s home' },
  { word: 'FIN',  clue: 'The flat wing-like part a fish uses to steer' },
  { word: 'GEM',  clue: 'A precious sparkling stone like a diamond' },
  { word: 'HOP',  clue: 'To jump on one foot, like a bunny' },
  { word: 'ICE',  clue: 'Frozen water that is cold and slippery' },
  { word: 'JUG',  clue: 'A container with a handle used to pour juice or water' },
  { word: 'KIT',  clue: 'A set of tools or supplies packed together' },
  { word: 'LID',  clue: 'The cover you put on top of a pot or jar' },
  { word: 'MOB',  clue: 'A big noisy crowd of people or animals' },
  { word: 'OAK',  clue: 'A large strong tree that grows acorns' },
  { word: 'PAW',  clue: 'The soft foot of a cat, dog, or bear' },
  { word: 'RUG',  clue: 'A small soft carpet you put on the floor' },
  { word: 'SAP',  clue: 'The sticky liquid that flows inside a tree' },
  { word: 'TAP',  clue: 'The handle you turn to get water from a pipe' },
  { word: 'URN',  clue: 'A tall decorative vase, often used for ashes' },
  { word: 'WAX',  clue: 'The substance that makes up a candle' },
]

const EN_YOUNG: WordEntry[] = [
  { word: 'STAR',    clue: 'A glowing ball of gas that twinkles in the night sky' },
  { word: 'BIRD',    clue: 'A feathered creature with wings that can fly' },
  { word: 'FISH',    clue: 'A scaly creature that breathes underwater through gills' },
  { word: 'RAIN',    clue: 'Drops of water that fall from clouds in the sky' },
  { word: 'SNOW',    clue: 'Soft white frozen water crystals that fall in winter' },
  { word: 'BOOK',    clue: 'Pages of words and pictures bound together to read' },
  { word: 'CAKE',    clue: 'A sweet baked treat covered in frosting for birthdays' },
  { word: 'FROG',    clue: 'A green jumping amphibian that lives near ponds' },
  { word: 'ROSE',    clue: 'A beautiful fragrant flower that grows on thorny stems' },
  { word: 'TREE',    clue: 'A tall plant with a wooden trunk, branches, and leaves' },
  { word: 'KITE',    clue: 'A diamond-shaped toy you fly in the wind on a long string' },
  { word: 'MOON',    clue: 'Earth\'s natural satellite that glows in the night sky' },
  { word: 'FIRE',    clue: 'Hot orange flames that give warmth and light' },
  { word: 'DOOR',    clue: 'A hinged panel you open or close to enter a room' },
  { word: 'BEAR',    clue: 'A large furry mammal that sleeps through winter' },
  { word: 'SHIP',    clue: 'A large ocean vessel powered by engines or sails' },
  { word: 'RING',    clue: 'A circular band of metal worn on a finger' },
  { word: 'NEST',    clue: 'A cup-shaped home a bird builds to lay its eggs in' },
  { word: 'DRUM',    clue: 'A round musical instrument you hit with sticks to make a beat' },
  { word: 'WOLF',    clue: 'A wild animal that howls at the moon and hunts in packs' },
  { word: 'FLAG',    clue: 'A colourful cloth on a pole that represents a country or team' },
  { word: 'BELL',    clue: 'A hollow metal object that makes a ringing sound when struck' },
  { word: 'DUCK',    clue: 'A water bird with a flat bill that says quack' },
  { word: 'LAMP',    clue: 'A device with a bulb that lights up a room' },
  { word: 'SAND',    clue: 'Tiny grains of crushed rock found on beaches and deserts' },
  { word: 'SEED',    clue: 'A tiny object you plant in the soil to grow a new plant' },
  { word: 'POND',    clue: 'A small shallow body of still water where frogs live' },
  { word: 'FARM',    clue: 'A piece of land with fields and animals kept for food' },
  { word: 'COIN',    clue: 'A flat round piece of metal used as money' },
  { word: 'MILK',    clue: 'A white nutritious drink that comes from cows' },
  { word: 'LEAF',    clue: 'The flat green part of a plant that captures sunlight' },
  { word: 'ROAD',    clue: 'A paved path that cars, trucks, and bikes travel on' },
  { word: 'HAND',    clue: 'The part of your arm below the wrist with five fingers' },
  { word: 'BALL',    clue: 'A round object you kick, throw, or bounce in sports' },
  { word: 'CRAB',    clue: 'A sea creature with a hard shell and two big claws' },
  { word: 'SWAN',    clue: 'A graceful white water bird with a long elegant neck' },
  { word: 'FAWN',    clue: 'A baby deer with spots on its back' },
  { word: 'MIST',    clue: 'A thin cloud of tiny water droplets close to the ground' },
  { word: 'GLOW',    clue: 'A soft steady light, like embers in a dying fire' },
  { word: 'HIVE',    clue: 'The box-shaped home where bees live and make honey' },
  { word: 'IRON',    clue: 'A heavy grey metal, or a device used to smooth clothes' },
  { word: 'JADE',    clue: 'A smooth green gemstone used to make jewellery' },
  { word: 'KNOT',    clue: 'A fastening made by tying rope or string together' },
  { word: 'LACE',    clue: 'A delicate woven fabric with an open pattern; also a shoelace' },
  { word: 'MARE',    clue: 'A female horse' },
  { word: 'NEWT',    clue: 'A small lizard-like amphibian that lives near water' },
  { word: 'OPAL',    clue: 'A shimmery gemstone that shows many colours' },
  { word: 'PATH',    clue: 'A narrow trail you walk along through a park or forest' },
  { word: 'QUIZ',    clue: 'A short test of knowledge with questions and answers' },
  { word: 'RAFT',    clue: 'A flat floating platform made of logs or inflatable tubes' },
  { word: 'SAIL',    clue: 'A large sheet of cloth that catches the wind to move a boat' },
  { word: 'TOAD',    clue: 'A lumpy warty amphibian that lives on dry land' },
  { word: 'VINE',    clue: 'A climbing plant with long flexible stems, like a grape plant' },
  { word: 'WREN',    clue: 'A tiny brown bird with a short upright tail' },
  { word: 'YOLK',    clue: 'The yellow part in the middle of an egg' },
  { word: 'ZINC',    clue: 'A shiny bluish-white metal used to protect iron from rust' },
  { word: 'ARCH',    clue: 'A curved structure that forms an opening in a wall or bridge' },
  { word: 'BARN',    clue: 'A large red farm building where animals and hay are kept' },
  { word: 'CLAM',    clue: 'A shellfish with two hinged shells that lives in sand' },
  { word: 'DUSK',    clue: 'The time just after sunset when the sky turns purple' },
  { word: 'ECHO',    clue: 'A sound that bounces back and repeats itself in a cave or valley' },
  { word: 'FOAM',    clue: 'A mass of tiny bubbles on the surface of water or soap' },
  { word: 'GUST',    clue: 'A sudden strong rush of wind' },
  { word: 'HARP',    clue: 'A large triangular musical instrument with many strings' },
  { word: 'IRIS',    clue: 'A tall purple or yellow flower; also the coloured part of your eye' },
  { word: 'KELP',    clue: 'Large brown seaweed that grows in underwater forests' },
  { word: 'LYNX',    clue: 'A wild cat with tufted ears and a short tail' },
  { word: 'MOTH',    clue: 'A night-flying insect attracted to light, similar to a butterfly' },
]

const EN_MIDDLE: WordEntry[] = [
  { word: 'PLANET',    clue: 'A large sphere of rock or gas that orbits a star' },
  { word: 'CASTLE',    clue: 'A large stone fortress with towers where kings and queens lived' },
  { word: 'BRIDGE',    clue: 'A structure built over a river or valley to allow crossing' },
  { word: 'FOREST',    clue: 'A dense area of land covered with many tall trees' },
  { word: 'FLOWER',    clue: 'The colourful bloom of a plant that attracts insects' },
  { word: 'DRAGON',    clue: 'A mythical reptile that breathes fire and hoards treasure' },
  { word: 'ROCKET',    clue: 'A cylindrical spacecraft that blasts off into orbit' },
  { word: 'SPIDER',    clue: 'An eight-legged arachnid that weaves silken webs to catch prey' },
  { word: 'MONKEY',    clue: 'A clever primate with a long tail that swings through trees' },
  { word: 'MIRROR',    clue: 'A polished surface that reflects an exact image of whatever faces it' },
  { word: 'BASKET',    clue: 'A woven container made of reeds or wicker for carrying things' },
  { word: 'SUMMER',    clue: 'The warmest season of the year, when school is out' },
  { word: 'WINTER',    clue: 'The coldest season, often bringing frost, ice, and snow' },
  { word: 'SPRING',    clue: 'The season after winter when flowers bloom and birds return' },
  { word: 'DESERT',    clue: 'A vast dry landscape with little rainfall and extreme temperatures' },
  { word: 'ISLAND',    clue: 'A piece of land completely surrounded by water' },
  { word: 'RIVER',     clue: 'A large natural flow of fresh water moving towards the sea' },
  { word: 'OCEAN',     clue: 'One of the vast bodies of deep salt water covering most of Earth' },
  { word: 'THUNDER',   clue: 'The loud rumbling sound that follows a flash of lightning' },
  { word: 'CRYSTAL',   clue: 'A solid mineral with a regular geometric shape and flat faces' },
  { word: 'COMPASS',   clue: 'A navigation instrument with a needle that always points north' },
  { word: 'LANTERN',   clue: 'A portable light with a handle, often used outdoors or in caves' },
  { word: 'CAPTAIN',   clue: 'The person in command of a ship, aircraft, or sports team' },
  { word: 'LIBRARY',   clue: 'A public building where books, films, and music are kept to borrow' },
  { word: 'VOLCANO',   clue: 'A mountain with an opening through which lava and ash erupt' },
  { word: 'PYRAMID',   clue: 'An ancient Egyptian stone tomb with triangular sides' },
  { word: 'ECLIPSE',   clue: 'When one celestial body passes in front of another, blocking its light' },
  { word: 'GLACIER',   clue: 'A slow-moving river of compacted ice that carves valleys' },
  { word: 'QUARTER',   clue: 'One of four equal parts of a whole' },
  { word: 'PATTERN',   clue: 'A repeated design, shape, or sequence of events' },
  { word: 'ANTENNA',   clue: 'A rod that sends or receives radio or TV signals; also an insect\'s feeler' },
  { word: 'BATTERY',   clue: 'A small device that stores and supplies electrical energy' },
  { word: 'CHAPTER',   clue: 'A numbered section within a book or story' },
  { word: 'DIAMOND',   clue: 'The hardest natural substance on Earth, used in jewellery' },
  { word: 'EMPEROR',   clue: 'The ruler of a large empire, higher in rank than a king' },
  { word: 'FEATHER',   clue: 'A lightweight growth that covers a bird\'s body and enables flight' },
  { word: 'GRAVITY',   clue: 'The invisible force that pulls objects toward the centre of the Earth' },
  { word: 'HARVEST',   clue: 'The gathering of ripe crops from the fields in autumn' },
  { word: 'JOURNEY',   clue: 'A long trip or expedition from one place to another' },
  { word: 'KINGDOM',   clue: 'A country or territory ruled by a king or queen' },
  { word: 'MAGNIFY',   clue: 'To make something appear larger than it really is' },
  { word: 'NETWORK',   clue: 'A system of connected things, like roads, computers, or people' },
  { word: 'OTTOMAN',   clue: 'A padded footstool or low seat without a back' },
  { word: 'PENGUIN',   clue: 'A flightless black-and-white seabird that lives in the Antarctic' },
  { word: 'RAINBOW',   clue: 'An arc of colours in the sky caused by light shining through rain' },
  { word: 'SHELTER',   clue: 'A place that provides protection from weather or danger' },
  { word: 'TIMBER',    clue: 'Wood that has been cut and prepared for use in building' },
  { word: 'TORNADO',   clue: 'A violent spinning column of air that touches the ground' },
  { word: 'TUNDRA',    clue: 'A vast flat treeless Arctic region with frozen ground' },
  { word: 'UNICORN',   clue: 'A mythical horse with a single spiralled horn on its forehead' },
  { word: 'VORTEX',    clue: 'A swirling mass of water, air, or fire that pulls things inward' },
  { word: 'WALRUS',    clue: 'A large marine mammal with long tusks that lives in the Arctic' },
  { word: 'WARRIOR',   clue: 'A brave and experienced fighter or soldier' },
  { word: 'WEATHER',   clue: 'The daily conditions of the atmosphere: sun, rain, wind, or snow' },
  { word: 'XENON',     clue: 'A colourless odourless gas used in electric lamps' },
  { word: 'YACHT',     clue: 'A sleek sailing or motor boat used for racing or cruising' },
  { word: 'ANCIENT',   clue: 'Something that existed or happened a very long time ago' },
  { word: 'BALANCE',   clue: 'Equal weight on both sides; also the act of staying steady' },
  { word: 'CLIMATE',   clue: 'The typical weather patterns in a region over many years' },
  { word: 'CURRENT',   clue: 'A flowing movement of water, air, or electricity' },
  { word: 'EROSION',   clue: 'The gradual wearing away of rock or soil by water or wind' },
  { word: 'HABITAT',   clue: 'The natural environment where a plant or animal normally lives' },
  { word: 'HORIZON',   clue: 'The distant line where the sky appears to meet the earth or sea' },
  { word: 'INSECT',    clue: 'A small creature with six legs and three body sections' },
  { word: 'MINERAL',   clue: 'A naturally occurring solid substance found in rocks and soil' },
  { word: 'PROTEIN',   clue: 'A nutrient found in meat, eggs, and beans that builds muscles' },
  { word: 'CIRCUIT',   clue: 'A complete path around which electricity can flow' },
  { word: 'OXYGEN',    clue: 'The gas in the air that all living things need to breathe' },
]

// ─── Slovenian ────────────────────────────────────────────────────────────────

const SL_TINY: WordEntry[] = [
  { word: 'PES',    clue: 'Zvest hišni ljubljenček, ki laja' },
  { word: 'MED',    clue: 'Sladka rumena tekočina, ki jo delajo čebele' },
  { word: 'SOL',    clue: 'Bela zrnatna snov, s katero začinimo hrano' },
  { word: 'LUČ',    clue: 'Sveti v temi; nasprotje teme' },
  { word: 'KOS',    clue: 'Del nečesa; tudi ptica pevka s črnimi peruti' },
  { word: 'LEP',    clue: 'Privlačen na pogled; nasprotje grdega' },
  { word: 'POT',    clue: 'Steza ali pot med dvema krajema' },
  { word: 'MIR',    clue: 'Stanje brez vojne ali hrupa; tišina' },
  { word: 'GOR',    clue: 'V smeri navzgor; v višino' },
  { word: 'DAR',    clue: 'Podarjeno darilo; naravna sposobnost' },
  { word: 'LAS',    clue: 'Tanka dlaka, ki raste na glavi' },
  { word: 'ROD',    clue: 'Vrsta ali sorta; generacija v družini' },
  { word: 'MOŽ',    clue: 'Odrasel moški; soprog' },
  { word: 'OKO',    clue: 'Organ za vid; z njim gledamo svet' },
  { word: 'NOS',    clue: 'Organ za vohanje in dihanje na sredini obraza' },
  { word: 'ROK',    clue: 'Določen čas; tudi del telesa od rame do dlani' },
  { word: 'ZOB',    clue: 'Trda bela kost v ustih za grizenje hrane' },
  { word: 'VES',    clue: 'Celoten; vsak del vključen' },
  { word: 'TLA',    clue: 'Površina tal; pod nogami' },
  { word: 'RAK',    clue: 'Morski živali s klešči; tudi bolezen' },
  { word: 'VOL',    clue: 'Kastriran bik, ki vleče pluge' },
  { word: 'KRT',    clue: 'Majhna živalca, ki živi pod zemljo in kopa rove' },
  { word: 'JEZ',    clue: 'Pregrada, ki zadrži vodo v reki ali potoku' },
  { word: 'LEV',    clue: 'Kralj živali z gosto grivo, ki lovi v afriški savani' },
  { word: 'MIŠ',    clue: 'Majhna sivkasta živalca z dolgim repom in tankimi brklji' },
  { word: 'BIK',    clue: 'Samec goveda z rogovi; moški spol krave' },
  { word: 'BOB',    clue: 'Okroglo zrnje stročnic; streljanje z malimi kroglicami' },
  { word: 'DIM',    clue: 'Sivi plin, ki nastane pri gorenju; megla dima' },
  { word: 'GIB',    clue: 'Premik telesa; gibanje rok ali nog' },
  { word: 'HOD',    clue: 'Premikanje s koraki; tempo hoje' },
  { word: 'JED',    clue: 'Hrana, ki jo pojemo; kosilo ali večerja' },
  { word: 'KUP',    clue: 'Velika količina ali svežanj nečesa; kup sena' },
  { word: 'LOV',    clue: 'Preganjanje divjadi v naravi; lovec gre na lov' },
  { word: 'MAJ',    clue: 'Peti mesec v letu; pomlad je v polnem razcvetu' },
  { word: 'NIT',    clue: 'Tanka vlakna, ki jo navijamo na tuljavo za šivanje' },
  { word: 'OSA',    clue: 'Rumeno-črna žuželka s pikastim zadkom, ki piči' },
  { word: 'DAN',    clue: 'Štiriindvajset ur; čas od jutra do naslednjega jutra' },
  { word: 'ROB',    clue: 'Zunanja meja nečesa; rob mize, jame ali prepada' },
  { word: 'SAJ',    clue: 'Črna snet, ki nastane pri gorenju; umazanija od ognja' },
  { word: 'TRS',    clue: 'Trta; rastlina, na kateri rastejo grozdi' },
  { word: 'UHO',    clue: 'Organ za sluh; z njim slišimo zvoke' },
  { word: 'VAL',    clue: 'Valovitost vode na morju ali jezeru' },
  { word: 'ZID',    clue: 'Navpična gradbena struktura iz opeke ali kamna' },
  { word: 'MAH',    clue: 'Mehka zelena rastlina, ki raste na mokrem kamnu ali drevesih' },
  { word: 'BOR',    clue: 'Zimzeleno iglasto drevo z dolgimi iglicami in storžki' },
]

const SL_YOUNG: WordEntry[] = [
  { word: 'VODA',    clue: 'Prozorna tekočina brez okusa, ki jo pijemo vsak dan' },
  { word: 'REKA',    clue: 'Naravni tok tekoče sladke vode, ki teče v morje' },
  { word: 'GORA',    clue: 'Visoka vzpetina s skalnato vrhom in snegom' },
  { word: 'MIZA',    clue: 'Pohištveni kos s ploščo in nogami, za katerim jemo ali delamo' },
  { word: 'LUNA',    clue: 'Naravni satelit Zemlje, ki ponoči sveti na nebu' },
  { word: 'DREVO',   clue: 'Visoka rastlina z lesenim deblom, vejami in listi' },
  { word: 'CESTA',   clue: 'Utrjena površina za promet avtomobilov in koles' },
  { word: 'ŠOLA',    clue: 'Ustanova, v kateri se otroci učijo brati, pisati in računati' },
  { word: 'MAMA',    clue: 'Ženska starša; oseba, ki nas neguje in ljubi' },
  { word: 'BRAT',    clue: 'Moški sorojenec; fant, ki ima iste starše kot ti' },
  { word: 'RIBE',    clue: 'Živali s plavutmi in luskami, ki dihajo v vodi' },
  { word: 'CVET',    clue: 'Barven del rastline, ki privlači čebele in metulje' },
  { word: 'MRAK',    clue: 'Čas prehoda med dnem in nočjo, ko nebo potemni' },
  { word: 'ZIMA',    clue: 'Najhladnejši letni čas z mrazom, snegom in ledom' },
  { word: 'POLJE',   clue: 'Ravna obdelana zemlja za pridelavo žita in zelenjave' },
  { word: 'GOZD',    clue: 'Veliko območje, poraslo z drevesi in grmičevjem' },
  { word: 'KOLO',    clue: 'Prevozno sredstvo z dvema kolesoma, ki ga poganjamo s pedali' },
  { word: 'NEBO',    clue: 'Modra ali siva površina nad nami, kjer so oblaki in zvezde' },
  { word: 'STAR',    clue: 'Nasprotje mladega; star je tisti, ki je živel že dolgo' },
  { word: 'MISEL',   clue: 'Ideja ali pomislek, ki nastane v možganih' },
  { word: 'SONCE',   clue: 'Zvezda, ki nam daje toploto in svetlobo podnevi' },
  { word: 'MORJE',   clue: 'Veliko telo slane vode ob obali; manjše od oceana' },
  { word: 'KAMEN',   clue: 'Trd kos mineralne snovi, ki jo najdemo v naravi' },
  { word: 'ZLATO',   clue: 'Dragocena rumena kovina, iz katere naredijo nakit in kovance' },
  { word: 'METLA',   clue: 'Orodje z dolgim ročajem in ščetinami za pometanje tal' },
  { word: 'OKNO',    clue: 'Steklena odprtina v zidu, skozi katero prihaja svetloba' },
  { word: 'TORBA',   clue: 'Vrečka z ročajem za prenašanje stvari' },
  { word: 'HIŠA',    clue: 'Stavba z zidovi in streho, v kateri živijo ljudje' },
  { word: 'KONJ',    clue: 'Velik žival s kopiti, ki ga jahamo ali vpregamo' },
  { word: 'MEDVED',  clue: 'Velika dlakava žival, ki prezimi v brlogu' },
  { word: 'LISICA',  clue: 'Zvita rdeča žival z bushnato repom, ki lovi zajce' },
  { word: 'ZAJEC',   clue: 'Majhna dlakava žival z dolgimi ušesi, ki skače' },
  { word: 'SRNA',    clue: 'Graciozna žival iz gozdov, ženski spol jelena' },
  { word: 'VOLK',    clue: 'Divja žival iz psje družine, ki tuli in lovi v tropih' },
  { word: 'OREL',    clue: 'Velika ptica ujeda z ostrimi kremplji in krivim kljunom' },
  { word: 'LABOD',   clue: 'Elegantna bela vodna ptica z dolgim vratom' },
  { word: 'GOLOB',   clue: 'Siva ptica, ki jo vidimo v mestih; simbol miru' },
  { word: 'METULJ',  clue: 'Insekt s pisanimi krili, ki nastane iz gosenice' },
  { word: 'ČEBELA',  clue: 'Rumeno-črni insekt, ki zbira nektar in dela med' },
  { word: 'POLŽ',    clue: 'Počasna živalca s spiralno hišico, ki pušča sled sluzi' },
  { word: 'MUHA',    clue: 'Mala leteča žuželka, ki pristaja na hrano' },
  { word: 'KRIVA',   clue: 'Ukrivljena; ni ravna; nasprotje ravne' },
  { word: 'SOLZA',   clue: 'Kaplja slane vode, ki steče iz oči ko jočemo' },
  { word: 'PESEK',   clue: 'Drobna zrna kamnin na plaži ali v puščavi' },
  { word: 'OBLAK',   clue: 'Bela ali siva masa vodnih kapljic na nebu' },
]

const SL_MIDDLE: WordEntry[] = [
  { word: 'PLANET',   clue: 'Nebesno telo, ki kroži okoli zvezde po določeni tirnici' },
  { word: 'VULKAN',   clue: 'Gora z odprtino, iz katere izbruhne vroča lava in pepel' },
  { word: 'OCEAN',    clue: 'Ogromno telo slane vode, ki pokriva večino Zemljine površine' },
  { word: 'ZAKLAD',   clue: 'Skrite dragocene stvari, ki jih je zakopan pirat ali kralj' },
  { word: 'RIBNIK',   clue: 'Majhno stoječe vodno telo, ki ga obkrožata trstičje in blatno dno' },
  { word: 'ZVEZDA',   clue: 'Ogromna žareča krogla plina, ki sveti na nočnem nebu' },
  { word: 'ZEMLJA',   clue: 'Tretji planet od Sonca; naš dom v vesolju' },
  { word: 'POTOK',    clue: 'Majhen naravni tok čiste vode po gozdnih tleh' },
  { word: 'VIHAR',    clue: 'Močna neurje z vetrom, dežjem in strelo' },
  { word: 'KAPLJA',   clue: 'Majhna sferična kolicina tekočine, ki pade z oblaka ali lista' },
  { word: 'SKALA',    clue: 'Velik trd kos kamna, ki stoji v naravi ali na pobočju' },
  { word: 'DOLINA',   clue: 'Nižina med gorami, po kateri teče reka ali potok' },
  { word: 'DREVESA',  clue: 'Množina besede drevo; visoke rastline z deblom in vejami' },
  { word: 'KOMET',    clue: 'Ledeno nebesno telo, ki kroži okoli Sonca in pušča svetel rep' },
  { word: 'PUŠČAVA',  clue: 'Suho območje z malo dežja, visokim podnebjem in peščenimi sipinami' },
  { word: 'JAMA',     clue: 'Naravna votlina v skali ali gori, pogosto s stalaktiti' },
  { word: 'SLADICA',  clue: 'Sladka jed po obroku; torta, sladoled ali kremšnita' },
  { word: 'BRODOLOM', clue: 'Potopitev ali razbijanje ladje ob skale ali v nevihti' },
  { word: 'GORAT',    clue: 'Pokrit z gorami; označuje pokrajino polno vzpetin' },
  { word: 'KOMPAS',   clue: 'Navigacijski instrument z iglo, ki vedno kaže proti severu' },
  { word: 'FOSIL',    clue: 'Ostanki davno izumrlih živali ali rastlin, ohranjeni v kamnu' },
  { word: 'KORALA',   clue: 'Morski organizem, ki gradi grebene v tropskih morjih' },
  { word: 'ORBITA',   clue: 'Tirnica, po kateri nebesno telo kroži okoli zvezde ali planeta' },
  { word: 'NEVIHTA',  clue: 'Slabo vreme z vetrom, dežjem, strelo in gromom' },
  { word: 'STALAKTIT',clue: 'Kamninska kapnica, ki visi s stropa v jami' },
  { word: 'VODOPAD',  clue: 'Tok vode, ki pada z visoke pečine v bazen spodaj' },
  { word: 'VESOLJE',  clue: 'Neskončni prostor z vsemi galaksijami, zvezdami in planeti' },
  { word: 'LEDENIK',  clue: 'Ogromna masa ledu, ki se počasi pomika po gorski dolini' },
  { word: 'MAGNETIT', clue: 'Naravni magnet; mineral, ki privlači kovino' },
  { word: 'BIOM',     clue: 'Geografska regija z značilnimi rastlinami in živalmi' },
]

// ─── Italian ──────────────────────────────────────────────────────────────────

const IT_TINY: WordEntry[] = [
  { word: 'GATTO',  clue: 'Animale domestico con le baffi che fa le fusa' },
  { word: 'CANE',   clue: 'Fedele amico dell\'uomo che abbaia' },
  { word: 'SOLE',   clue: 'La stella che brilla nel cielo di giorno' },
  { word: 'MARE',   clue: 'Grande distesa di acqua salata dove nuotano i pesci' },
  { word: 'PANE',   clue: 'Alimento morbido fatto con farina e acqua, cotto al forno' },
  { word: 'MELA',   clue: 'Frutto rotondo rosso o verde con un sapore dolce e acidulo' },
  { word: 'ROSA',   clue: 'Fiore profumato con i petali morbidi e le spine sullo stelo' },
  { word: 'TOPO',   clue: 'Piccolo roditore grigio con la coda lunga e le orecchie grandi' },
  { word: 'NASO',   clue: 'La parte del viso con cui respiriamo e sentiamo gli odori' },
  { word: 'LUCE',   clue: 'La cosa che ci permette di vedere nel buio' },
  { word: 'RANA',   clue: 'Anfibio verde che gracchia e salta nelle pozzanghere' },
  { word: 'NIDO',   clue: 'La casa che costruiscono gli uccelli per covare le uova' },
  { word: 'ORSO',   clue: 'Grande animale peloso che va in letargo d\'inverno' },
  { word: 'MANO',   clue: 'La parte del corpo con cinque dita attaccata al polso' },
  { word: 'BUCA',   clue: 'Un\'apertura o uno scavo nel terreno' },
  { word: 'DITO',   clue: 'Una delle cinque appendici della mano o del piede' },
  { word: 'LAGO',   clue: 'Una distesa d\'acqua dolce circondata da terra' },
  { word: 'NEVE',   clue: 'Fiocchi bianchi gelati che cadono dal cielo d\'inverno' },
  { word: 'FUMO',   clue: 'Gas scuro e grigio che sale da un fuoco o da un camino' },
  { word: 'DADO',   clue: 'Piccolo cubo con i numeri da 1 a 6 usato nei giochi' },
  { word: 'FARO',   clue: 'Torre alta vicino al mare con una luce che guida le navi' },
  { word: 'GELO',   clue: 'Il freddo intenso che trasforma l\'acqua in ghiaccio' },
  { word: 'ERBA',   clue: 'Le piante verdi basse che coprono i prati e i giardini' },
  { word: 'LANA',   clue: 'Fibra morbida e calda che proviene dalla pecora' },
  { word: 'MUTO',   clue: 'Chi non può parlare; anche chi è silenzioso' },
  { word: 'PALO',   clue: 'Un bastone lungo e dritto piantato nel terreno' },
  { word: 'RAMO',   clue: 'Parte dell\'albero che si stacca dal tronco' },
  { word: 'SALE',   clue: 'Sostanza bianca granulare usata per condire i cibi' },
  { word: 'TELA',   clue: 'Tessuto su cui i pittori dipingono; anche la ragnatela' },
  { word: 'UOVA',   clue: 'Plurale di uovo; le covano le galline e altri uccelli' },
  { word: 'VELA',   clue: 'Grande telo che cattura il vento per far muovere una barca' },
  { word: 'LUPO',   clue: 'Animale selvatico simile al cane che vive in branco e ulula alla luna' },
  { word: 'ARCO',   clue: 'Struttura curva; anche lo strumento per suonare il violino' },
  { word: 'BOSCO',  clue: 'Un\'area con molti alberi; più piccolo di una foresta' },
  { word: 'CAPRA',  clue: 'Animale da fattoria con le corna che fornisce latte' },
  { word: 'DONNA',  clue: 'Una persona adulta di sesso femminile' },
  { word: 'FIORE',  clue: 'La parte colorata e profumata di una pianta' },
  { word: 'GONNA',  clue: 'Indumento femminile che copre la parte inferiore del corpo' },
  { word: 'ISOLA',  clue: 'Pezzo di terra completamente circondato dall\'acqua' },
]

const IT_YOUNG: WordEntry[] = [
  { word: 'FIUME',    clue: 'Corso d\'acqua dolce che scorre dai monti verso il mare' },
  { word: 'MONTE',    clue: 'Grande altura di roccia e terra con la cima innevata' },
  { word: 'NUVOLA',   clue: 'Ammasso di vapore acqueo bianco che galleggia nel cielo' },
  { word: 'STELLE',   clue: 'Puntini luminosi che brillano nel cielo notturno' },
  { word: 'ALBERO',   clue: 'Pianta alta con il tronco di legno, rami e foglie verdi' },
  { word: 'GATTO',    clue: 'Animale domestico con il pelo morbido che fa le fusa' },
  { word: 'PORTA',    clue: 'Pannello che apriamo e chiudiamo per entrare in una stanza' },
  { word: 'LUNA',     clue: 'Il satellite naturale della Terra che brilla di notte' },
  { word: 'VERDE',    clue: 'Il colore delle foglie, dell\'erba e delle rane' },
  { word: 'LIBRO',    clue: 'Raccolta di pagine stampate con storie o informazioni' },
  { word: 'PIOGGIA',  clue: 'Acqua che cade dal cielo sotto forma di gocce' },
  { word: 'VENTO',    clue: 'Il movimento dell\'aria che fa volare aquiloni e foglie' },
  { word: 'PIETRA',   clue: 'Pezzo solido di roccia che si trova per terra o nei fiumi' },
  { word: 'ESTATE',   clue: 'La stagione più calda dell\'anno con giorni lunghi e sole' },
  { word: 'INVERNO',  clue: 'La stagione più fredda con neve, ghiaccio e notti lunghe' },
  { word: 'COLORE',   clue: 'Qualità visiva come il rosso, il blu, il giallo o il verde' },
  { word: 'FARFALLA', clue: 'Insetto con le ali colorate che si nutre di nettare dei fiori' },
  { word: 'CASTELLO', clue: 'Grande fortezza medievale con torri dove vivevano i re' },
  { word: 'TORTORA',  clue: 'Piccione grigio-rosa che canta con voce dolce e melodiosa' },
  { word: 'CONIGLIO', clue: 'Piccolo mammifero bianco con le orecchie lunghe e la coda a pom pom' },
  { word: 'GIARDINO', clue: 'Spazio verde intorno a una casa con fiori, piante e prati' },
  { word: 'BAMBINO',  clue: 'Un essere umano giovane che non è ancora adulto' },
  { word: 'CORNICE',  clue: 'Bordo decorativo intorno a un quadro o una fotografia' },
  { word: 'FINESTRA', clue: 'Apertura nella parete con il vetro che lascia entrare la luce' },
  { word: 'FORMICHE', clue: 'Insetti piccoli che vivono in colonie sotto terra' },
  { word: 'LUCCIOLA', clue: 'Insetto che brilla nella notte emettendo luce verde' },
  { word: 'PECORA',   clue: 'Animale da fattoria con il vello bianco che dà lana e latte' },
  { word: 'ARCOBALENO',clue: 'Arco di sette colori nel cielo dopo la pioggia con il sole' },
  { word: 'PAPPAGALLO',clue: 'Uccello tropicale colorato che sa imitare la voce umana' },
  { word: 'TARTARUGA', clue: 'Rettile lento con il guscio duro che la protegge dai predatori' },
  { word: 'GIRASOLE',  clue: 'Fiore alto con petali gialli che si gira sempre verso il sole' },
  { word: 'COCCODRILLO',clue: 'Rettile acquatico verde con denti affilati che vive nei fiumi tropicali' },
  { word: 'ELEFANTE',  clue: 'Il più grande animale terrestre con la proboscide e le zanne' },
  { word: 'GIRAFFA',   clue: 'Animale africano con il collo lunghissimo per raggiungere le foglie' },
  { word: 'SCIMMIA',   clue: 'Primate intelligente che vive sugli alberi e mangia banane' },
  { word: 'GRANCHIO',  clue: 'Crostaceo marino con le chele che cammina di lato' },
  { word: 'DELFINO',   clue: 'Mammifero marino intelligente e giocoso che salta nell\'acqua' },
  { word: 'VOLPE',     clue: 'Animale furbo con il manto arancione e la coda folta' },
  { word: 'RICCIO',    clue: 'Piccolo animale notturno coperto di spine appuntite' },
  { word: 'LEPRE',     clue: 'Mammifero selvatico simile al coniglio ma più grande e veloce' },
]

const IT_MIDDLE: WordEntry[] = [
  { word: 'PIANETA',    clue: 'Corpo celeste che orbita attorno a una stella seguendo una traiettoria ellittica' },
  { word: 'VULCANO',    clue: 'Montagna con cratere che erutta lava, cenere e gas bollenti' },
  { word: 'DESERTO',    clue: 'Zona arida con pochissima pioggia, temperature estreme e sabbia' },
  { word: 'OCEANO',     clue: 'Vasta distesa di acqua salata che copre la maggior parte della Terra' },
  { word: 'BUSSOLA',    clue: 'Strumento di navigazione con ago magnetico che indica sempre il Nord' },
  { word: 'LANTERNA',   clue: 'Lampada portatile con manico e vetro protettivo intorno alla fiamma' },
  { word: 'CAPITANO',   clue: 'Comandante di una nave, aereo o squadra sportiva' },
  { word: 'PIRAMIDE',   clue: 'Monumentale tomba egizia a forma di triangolo costruita con enormi blocchi' },
  { word: 'GALASSIA',   clue: 'Enorme sistema di miliardi di stelle, gas e polvere cosmica' },
  { word: 'FORESTA',    clue: 'Grande area ricoperta di alberi ad alto fusto, casa di molti animali' },
  { word: 'CASCATA',    clue: 'Flusso d\'acqua che precipita verticalmente da una grande altezza' },
  { word: 'STAGIONE',   clue: 'Ognuno dei quattro periodi annuali: primavera, estate, autunno, inverno' },
  { word: 'ORIZZONTE',  clue: 'Linea immaginaria dove il cielo sembra toccare la terra o il mare' },
  { word: 'SENTIERO',   clue: 'Piccolo percorso stretto che si snoda attraverso boschi e montagne' },
  { word: 'TERREMOTO',  clue: 'Violente scosse del suolo causate dal movimento delle placche tettoniche' },
  { word: 'ASTEROIDE',  clue: 'Piccolo corpo roccioso che orbita attorno al Sole tra Marte e Giove' },
  { word: 'ATMOSFERA',  clue: 'Strato di gas che avvolge la Terra e la protegge dai raggi solari' },
  { word: 'CONTINENTE', clue: 'Ognuna delle sette grandi masse terrestri del pianeta Terra' },
  { word: 'GRAVITAZIONE',clue: 'Forza invisibile che attrae i corpi verso il centro della Terra' },
  { word: 'MAGNETE',    clue: 'Oggetto che attrae il ferro e altri metalli grazie al suo campo magnetico' },
  { word: 'CRISTALLO',  clue: 'Solido minerale con struttura geometrica regolare e facce piatte lucenti' },
  { word: 'ECUATORE',   clue: 'Linea immaginaria che divide la Terra in emisfero nord e sud' },
  { word: 'FOSSILE',    clue: 'Resti o impronta di organismi preistorici conservati nella roccia per milioni di anni' },
  { word: 'TSUNAMI',    clue: 'Gigantesca onda marina causata da un terremoto sottomarino' },
  { word: 'SATELLITE',  clue: 'Oggetto naturale o artificiale che orbita intorno a un pianeta' },
  { word: 'MERIDIANO',  clue: 'Linea immaginaria verticale che va dal Polo Nord al Polo Sud' },
  { word: 'PARALLELO',  clue: 'Linea immaginaria orizzontale parallela all\'equatore sulla mappa' },
  { word: 'EROSIONE',   clue: 'Graduale consumo di rocce e suolo causato da acqua, vento e ghiaccio' },
  { word: 'CORALLO',    clue: 'Piccolo organismo marino che forma barriere coralline nei mari tropicali' },
  { word: 'GHIACCIAIO', clue: 'Enorme massa di ghiaccio compresso che si muove lentamente verso il mare' },
]

// ─── Lookup ───────────────────────────────────────────────────────────────────

const BANKS: Record<Language, Record<AgeGroup, WordEntry[]>> = {
  en: { tiny: EN_TINY, young: EN_YOUNG, middle: EN_MIDDLE },
  sl: { tiny: SL_TINY, young: SL_YOUNG, middle: SL_MIDDLE },
  it: { tiny: IT_TINY, young: IT_YOUNG, middle: IT_MIDDLE },
}

export function getWordBank(language: Language, ageGroup: AgeGroup): WordEntry[] {
  return BANKS[language][ageGroup] ?? EN_YOUNG
}
